import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { fetchUserDashboard } from '../../api/admin';
import { useCertStore } from '../../stores/certStore';
import type { UserDashboardData } from '../../types/admin';

interface QuickStat {
  label: string;
  value: string | number;
}

export default function StudyHeader() {
  const { t } = useTranslation();
  const certCode = useCertStore((s) => s.certCode);
  const certName = useCertStore((s) => s.certName);

  const { data: dashboard } = useQuery<UserDashboardData>({
    queryKey: ['user-dashboard', certCode],
    queryFn: () => fetchUserDashboard(certCode),
    staleTime: 60_000,
  });

  const ov = dashboard?.overview;

  const stats: QuickStat[] = ov
    ? [
        { label: t('dashboard.study.control.stats.sessions'), value: ov.study_count },
        { label: t('dashboard.study.control.stats.answered'), value: ov.total_answered },
        { label: t('dashboard.study.control.stats.accuracy'), value: `${ov.overall_score}%` },
        { label: t('dashboard.study.control.stats.streak'), value: t('dashboard.study.control.stats.streakValue', { count: ov.current_streak }) },
      ]
    : [];

  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white sm:text-3xl">
          {t('dashboard.study.control.headerTitle')}
        </h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {certName
            ? t('dashboard.study.control.headerSubtitleWithCert', { certName })
            : t('dashboard.study.control.headerSubtitleFallback')}
        </p>
      </div>

      {stats.length > 0 && (
        <div className="flex gap-3 sm:gap-4">
          {stats.map((s) => (
            <div
              key={s.label}
              className="flex flex-col items-center rounded-xl border border-slate-200/70 bg-white px-3 py-2 shadow-sm dark:border-white/10 dark:bg-card-dark"
            >
              <span className="font-mono text-lg font-bold text-slate-900 dark:text-white tabular-nums">
                {s.value}
              </span>
              <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
                {s.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
